import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getSignals, getCandles, getPositions, getHistoricalCandles, getQuote, getTrades, getMarketRegime, getInstrumentSearch, getTradeBlacklist, addTradeBlacklistSymbol, removeTradeBlacklistSymbol } from "../api";
import CandleChart from "../components/CandleChart";
import SignalBadge from "../components/SignalBadge";
import StatCard from "../components/StatCard";
import useMediaQuery from "../hooks/useMediaQuery";
import { addDaysToYmd, previousYmdIST, periodRangeIst, currentPeriodValueIST } from "../utils/time";

const FALLBACK_SYMBOLS = ["RELIANCE", "TCS", "INFY", "HDFCBANK", "ICICIBANK"];
const CHART_RANGES = {
  "1D": { label: "1D", interval: "5minute", days: 1, liveFirst: true },
  "5D": { label: "5D", interval: "15minute", days: 5, liveFirst: false },
  "1M": { label: "1M", interval: "60minute", days: 30, liveFirst: false },
  "6M": { label: "6M", interval: "day", days: 180, liveFirst: false },
  "1Y": { label: "1Y", interval: "day", days: 364, liveFirst: false },
};

export default function Dashboard({ botStatus, selectedSymbol, selectedInstrumentKey, selectedTrade, selectedRequestId, returnPage, onBack }) {
  const isMobile = useMediaQuery("(max-width: 760px)");
  const [signals, setSignals]     = useState([]);
  const [selected, setSelected]   = useState("");
  const [candles, setCandles]     = useState([]);
  const [positions, setPositions] = useState({});
  const [trades, setTrades] = useState([]);
  const [profitPeriod, setProfitPeriod] = useState("week");
  const [profitPeriodValue, setProfitPeriodValue] = useState(() => currentPeriodValueIST("week"));
  const [profitCustomToDate, setProfitCustomToDate] = useState(() => currentPeriodValueIST("day"));
  const [latestQuote, setLatestQuote] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [candleSource, setCandleSource] = useState("live"); // "live" | "intraday" | "historical" | "none"
  const [chartRange, setChartRange] = useState("1M");
  const [chartMode, setChartMode] = useState("line");
  const [chartView, setChartView] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchedSymbols, setSearchedSymbols] = useState({});
  const [blacklistSymbols, setBlacklistSymbols] = useState([]);
  const [blacklistReady, setBlacklistReady] = useState(false);
  const [blacklistBusy, setBlacklistBusy] = useState(false);
  const [blacklistNotice, setBlacklistNotice] = useState("");
  const [blacklistError, setBlacklistError] = useState("");
  const [marketRegime, setMarketRegime] = useState(() => botStatus?.marketRegime?.latest || null);
  const [marketRegimeBusy, setMarketRegimeBusy] = useState(false);
  const [marketRegimeError, setMarketRegimeError] = useState("");
  const blacklistRequestRef = useRef(0);
  const blacklistMutationRef = useRef(false);
  const [chartSize, setChartSize] = useState({ width: 760, height: 280 });
  const candleCacheRef = useRef({});
  const externalSymbolRef = useRef(null);
  const externalInstrumentKeyRef = useRef("");

  const refreshBlacklist = useCallback(async () => {
    if (blacklistMutationRef.current) return;
    const request = ++blacklistRequestRef.current;
    try {
      const result = await getTradeBlacklist();
      if (request !== blacklistRequestRef.current) return;
      setBlacklistSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
      setBlacklistReady(true);
      setBlacklistError("");
    } catch (err) {
      if (request !== blacklistRequestRef.current) return;
      setBlacklistReady(false);
      setBlacklistError(err?.message || "Failed to load blacklist");
    }
  }, []);

  useEffect(() => {
    refreshBlacklist();
    window.addEventListener("focus", refreshBlacklist);
    return () => {
      ++blacklistRequestRef.current;
      window.removeEventListener("focus", refreshBlacklist);
    };
  }, [refreshBlacklist, selected]);

  const toggleSelectedBlacklist = async () => {
    const symbol = String(selected || "").trim().toUpperCase();
    if (!symbol || !blacklistReady || blacklistMutationRef.current) return;
    const removing = blacklistSymbols.includes(symbol);
    blacklistMutationRef.current = true;
    ++blacklistRequestRef.current;
    setBlacklistBusy(true);
    setBlacklistNotice("");
    setBlacklistError("");
    try {
      const result = removing
        ? await removeTradeBlacklistSymbol(symbol)
        : await addTradeBlacklistSymbol(symbol);
      setBlacklistSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
      setBlacklistNotice(`${symbol} ${removing ? "removed from" : "added to"} blacklist.`);
    } catch (err) {
      setBlacklistError(err?.message || `Failed to update blacklist for ${symbol}`);
    } finally {
      blacklistMutationRef.current = false;
      setBlacklistBusy(false);
    }
  };

  const watchKey = useMemo(() => {
    const wl = botStatus?.watchlist;
    if (!Array.isArray(wl) || !wl.length) return "";
    return wl.map((w) => w.symbol).join("|");
  }, [botStatus?.watchlist]);

  const watchlistSymbols = useMemo(() => {
    const wl = botStatus?.watchlist;
    if (!Array.isArray(wl) || !wl.length) return FALLBACK_SYMBOLS;
    return wl.map((w) => w.symbol).filter(Boolean);
  }, [watchKey]);

  const watchlistMeta = useMemo(() => {
    const wl = botStatus?.watchlist;
    if (!Array.isArray(wl)) return {};
    return Object.fromEntries(wl.map((w) => [w.symbol, w]));
  }, [watchKey]);
  const serverPreviousDate = botStatus?.marketClock?.previousDate || previousYmdIST();
  const chartRangeConfig = CHART_RANGES[chartRange] || CHART_RANGES["1M"];
  const selectedTradeForSymbol = selected === String(selectedSymbol || "").trim().toUpperCase() ? selectedTrade : null;

  const refreshMarketRegime = useCallback(async (force = false) => {
    setMarketRegimeBusy(true);
    try {
      const data = await getMarketRegime(force ? { refresh: "true" } : undefined);
      setMarketRegime(data?.latest || null);
      setMarketRegimeError("");
    } catch (error) {
      setMarketRegimeError(error?.message || "Market data refresh failed.");
    } finally {
      setMarketRegimeBusy(false);
    }
  }, []);

  useEffect(() => {
    if (botStatus?.marketRegime?.latest) setMarketRegime(botStatus.marketRegime.latest);
  }, [botStatus?.marketRegime?.latest]);

  useEffect(() => {
    refreshMarketRegime();
    if (!botStatus?.marketOpen) return undefined;
    const timer = setInterval(() => refreshMarketRegime(), 30_000);
    return () => clearInterval(timer);
  }, [refreshMarketRegime, botStatus?.marketOpen]);

  const enabledStrategies = useMemo(() => {
    const labels = {
      momentum: "Momentum",
      maCrossover: "MA Crossover",
      meanReversion: "Mean Reversion",
      macd: "MACD",
      mercury: "Mercury CPR/EMA/VWAP",
    };
    return Object.entries(botStatus?.strategies || {})
      .filter(([, enabled]) => enabled)
      .map(([key]) => labels[key] || key);
  }, [botStatus?.strategies]);

  const profitRangeValue = useMemo(() => (
    profitPeriod === "custom"
      ? { fromDate: profitPeriodValue, toDate: profitCustomToDate }
      : profitPeriodValue
  ), [profitPeriod, profitPeriodValue, profitCustomToDate]);
  const realizedForPeriod = useMemo(() => {
    const range = periodRangeIst(profitPeriod, profitRangeValue);
    return trades.reduce((sum, t) => {
      const time = new Date(t.timestamp || t.exitedAt || 0).getTime();
      return time >= range.start && time < range.end ? sum + Number(t.pnl || 0) : sum;
    }, 0);
  }, [profitPeriod, profitRangeValue, trades]);

  const openNetPnl = useMemo(() =>
    Object.values(positions).reduce((sum, pos) => sum + Number(pos.netPnl ?? ((pos.currentPrice || pos.entry || 0) - (pos.entry || 0)) * (pos.qty || 0)), 0),
  [positions]);
  const selectedRange = periodRangeIst(profitPeriod, profitRangeValue);
  const periodNetPnl = realizedForPeriod + (selectedRange.includesPresent ? openNetPnl : 0);
  const handleProfitPeriodChange = (period) => {
    setProfitPeriod(period);
    setProfitPeriodValue(currentPeriodValueIST(period));
    if (period === "custom") setProfitCustomToDate(currentPeriodValueIST("day"));
  };

  useEffect(() => {
    if (!watchlistSymbols.length) return;
    if (selectedSymbol) return;
    if (searchedSymbols[String(selected || "").trim().toUpperCase()]?.instrumentKey) return;
    if (!watchlistSymbols.includes(selected)) setSelected(watchlistSymbols[0]);
  }, [watchlistSymbols, selected, selectedSymbol, searchedSymbols]);

  useEffect(() => {
    const sym = String(selectedSymbol || "").trim().toUpperCase();
    const instrumentKey = String(selectedInstrumentKey || "").trim();
    if (sym && (sym !== externalSymbolRef.current || instrumentKey !== externalInstrumentKeyRef.current || selectedRequestId)) {
      externalSymbolRef.current = sym;
      externalInstrumentKeyRef.current = instrumentKey;
      candleCacheRef.current = {};
      setCandles([]);
      setLatestQuote(null);
      setChartView(null);
      setSelected(sym);
    }
  }, [selectedSymbol, selectedInstrumentKey, selectedRequestId]);

  useEffect(() => {
    if (!selectedTradeForSymbol?.entryTime) return;
    const rawEntryMs = Number(selectedTradeForSymbol.entryTime);
    const entryMs = Number.isFinite(rawEntryMs) ? rawEntryMs : new Date(selectedTradeForSymbol.entryTime).getTime();
    const endMs = selectedTradeForSymbol.exitTime ? new Date(selectedTradeForSymbol.exitTime).getTime() : Date.now();
    if (!Number.isFinite(entryMs) || !Number.isFinite(endMs)) return;
    const days = Math.max(1, Math.ceil((endMs - entryMs) / 86400000) + 5);
    setChartRange(days <= 30 ? "1M" : days <= 180 ? "6M" : "1Y");
  }, [selectedTradeForSymbol]);

  const fetchAll = useCallback(async () => {
    try {
      const [sigs, pos, tr] = await Promise.all([getSignals(), getPositions(), getTrades()]);
      setSignals(Array.isArray(sigs) ? sigs : []);
      setPositions(pos || {});
      setTrades(Array.isArray(tr) ? tr : []);
    } catch {}
  }, []);

  const selectedSig = signals.find(s => s.symbol === selected);
  const selectedPos = positions[selected];
  const selectedSymbolProp = String(selectedSymbol || "").trim().toUpperCase();
  const externalInstrumentKeyForSelected = selected === selectedSymbolProp ? String(selectedInstrumentKey || "").trim() : "";
  const selectedInstrumentKeyForApi =
    externalInstrumentKeyForSelected ||
    searchedSymbols[selected]?.instrumentKey ||
    watchlistMeta[selected]?.instrumentKey ||
    selectedPos?.instrumentKey ||
    selectedSig?.instrumentKey ||
    "";

  const handleSearch = useCallback(async (value) => {
    const q = String(value || "").trim().toUpperCase();
    setSearchQ(q);
    if (q.length < 1) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const data = await getInstrumentSearch(q);
      setSearchResults(Array.isArray(data?.matches) ? data.matches.slice(0, 12) : []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const openSearchedSymbol = useCallback((match) => {
    const sym = String(match?.symbol || "").trim().toUpperCase();
    if (!sym) return;
    candleCacheRef.current = {};
    setSearchedSymbols((prev) => ({ ...prev, [sym]: match }));
    setSelected(sym);
    setSearchQ("");
    setSearchResults([]);
    setLatestQuote(null);
    setChartView(null);
  }, []);
  const tradeMarkers = useMemo(() => {
    if (!selectedTradeForSymbol) return [];
    const buyPrice = Number(selectedTradeForSymbol.buyPrice);
    const sellPrice = Number(selectedTradeForSymbol.sellPrice);
    return [
      Number.isFinite(buyPrice)
        ? {
            type: "entry",
            label: "BUY",
            time: selectedTradeForSymbol.entryTime || null,
            price: buyPrice,
            color: "#22c55e",
          }
        : null,
      Number.isFinite(sellPrice)
        ? {
            type: "exit",
            label: "SELL",
            time: selectedTradeForSymbol.exitTime || null,
            price: sellPrice,
            color: "#ef4444",
          }
        : null,
    ].filter(Boolean);
  }, [selectedTradeForSymbol]);

  const handleChartViewChange = useCallback((next) => {
    setChartView((prev) => {
      if (
        prev &&
        prev.start === next.start &&
        prev.end === next.end &&
        prev.count === next.count &&
        prev.startTime === next.startTime &&
        prev.endTime === next.endTime
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  // Fetch candles: try live WS candles first, fall back to historical OHLC from Upstox API
  const fetchCandles = useCallback(async (sym, instrumentKey = "", rangeKey = chartRange) => {
    if (!sym) {
      setCandles([]);
      setCandleSource("none");
      setLoading(false);
      return;
    }
    const normalizedInstrumentKey = String(instrumentKey || "").trim();
    const cfg = CHART_RANGES[rangeKey] || CHART_RANGES["1M"];
    const cacheKey = `${sym}|${normalizedInstrumentKey}|${cfg.interval}|${cfg.days}`;
    const hit = candleCacheRef.current[cacheKey];
    if (hit && Date.now() - hit.ts < 15000) {
      setCandles(hit.data);
      setCandleSource(hit.source);
      return;
    }

    setLoading(true);
    try {
      // 1. Try live WebSocket candles (only available during market hours when bot is active)
      if (cfg.liveFirst) {
        const live = await getCandles(sym, 240);
        if (Array.isArray(live) && live.length > 5) {
          setCandles(live);
          setCandleSource("live");
          candleCacheRef.current[cacheKey] = { data: live, source: "live", ts: Date.now() };
          setLoading(false);
          return;
        }
      }

      // 2. Fall back to historical daily candles from Upstox (works anytime, requires login)
      if (botStatus?.authenticated) {
        try {
          const hist = await getHistoricalCandles({
            symbol: sym,
            instrumentKey: normalizedInstrumentKey || undefined,
            interval: cfg.interval,
            toDate: serverPreviousDate,
            fromDate: addDaysToYmd(serverPreviousDate, -cfg.days),
          });
          const histCandles = Array.isArray(hist?.candles) ? hist.candles : [];
          if (histCandles.length > 5) {
            setCandles(histCandles);
            setCandleSource(cfg.interval === "day" ? "historical" : "intraday");
            candleCacheRef.current[cacheKey] = { data: histCandles, source: cfg.interval === "day" ? "historical" : "intraday", ts: Date.now() };
            setLoading(false);
            return;
          }
        } catch {}
      }

      // 3. Nothing available
      setCandles([]);
      setCandleSource("none");
    } catch {
      setCandles([]);
      setCandleSource("none");
    }
    setLoading(false);
  }, [botStatus?.authenticated, serverPreviousDate, chartRange]);

  useEffect(() => {
    fetchAll();
    if (!botStatus?.marketOpen) return undefined;
    const t = setInterval(fetchAll, 3000);
    return () => clearInterval(t);
  }, [fetchAll, botStatus?.marketOpen]);

  useEffect(() => {
    const updateChartSize = () => {
      if (typeof window === "undefined") return;
      setChartSize({
        width: Math.max(640, Math.floor(window.innerWidth * (isMobile ? 0.9 : 0.58))),
        height: isMobile ? 300 : 340,
      });
    };
    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, [isMobile]);

  useEffect(() => {
    fetchCandles(selected, selectedInstrumentKeyForApi, chartRange);
  }, [selected, selectedInstrumentKeyForApi, chartRange, fetchCandles, selectedRequestId]);

  useEffect(() => {
    let cancelled = false;
    const fetchQuote = async () => {
      if (!botStatus?.authenticated || !selected || !botStatus?.marketOpen) {
        return;
      }
      try {
        const q = await getQuote(selected, { instrumentKey: selectedInstrumentKeyForApi || undefined });
        if (!cancelled) setLatestQuote(q);
      } catch {
        if (!cancelled) setLatestQuote(null);
      }
    };
    fetchQuote();
    if (!botStatus?.marketOpen) return () => { cancelled = true; };
    const t = setInterval(fetchQuote, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [selected, selectedInstrumentKeyForApi, botStatus?.authenticated, botStatus?.marketOpen]);

  // Re-fetch candles every 60s (live candles update; historical cached 15s)
  useEffect(() => {
    if (!botStatus?.marketOpen) return undefined;
    const t = setInterval(() => fetchCandles(selected, selectedInstrumentKeyForApi, chartRange), 60000);
    return () => clearInterval(t);
  }, [selected, selectedInstrumentKeyForApi, chartRange, fetchCandles, botStatus?.marketOpen]);

  const positionLtp = Number(selectedPos?.currentPrice);
  const quoteLtp    = latestQuote?.symbol === selected ? Number(latestQuote?.ltp || latestQuote?.lastPrice) : null;
  const signalPrice = Number(selectedSig?.lastPrice || selectedSig?.price);
  const candlePrice = candles.length ? Number(candles[candles.length - 1]?.close) : null;
  const price       = Number.isFinite(positionLtp) && positionLtp > 0
    ? positionLtp
    : Number.isFinite(quoteLtp) && quoteLtp > 0
      ? quoteLtp
      : Number.isFinite(signalPrice) && signalPrice > 0
        ? signalPrice
        : candlePrice;
  const prevPrice   = candles.length > 1 ? candles[candles.length - 2]?.close : null;
  const chgPct      = selectedPos?.entry && price
    ? ((price - selectedPos.entry) / selectedPos.entry * 100)
    : price && prevPrice ? ((price - prevPrice) / prevPrice * 100) : 0;
  const chartReturnPct = Number(chartView?.changePct);
  const chartReturnAbs = Number(chartView?.change);
  const tradeNetPct = Number(selectedTradeForSymbol?.netProfitPct);

  const buys  = signals.filter(s => s.action?.includes("BUY")).length;
  const sells = signals.filter(s => s.action?.includes("SELL")).length;
  const holds = signals.filter(s => s.action === "HOLD").length;
  const marketState = String(marketRegime?.state || "UNAVAILABLE").toUpperCase();
  const marketColor = marketState === "NORMAL" ? "#22c55e" : marketState === "RISK_OFF" ? "#f59e0b" : marketState === "STRESS" ? "#ef4444" : "#94a3b8";
  const marketIndexes = Array.isArray(marketRegime?.indexes) ? marketRegime.indexes : [];
  const missingMarketIndexes = Array.isArray(marketRegime?.quoteDiagnostics?.missingIndexes) ? marketRegime.quoteDiagnostics.missingIndexes : [];
  const marketAnalysis = marketRegime?.analysis || { headline: "Waiting for market data", summary: marketRegime?.reason || "No market-index snapshot has been received.", guidance: "" };

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 300px", gap: 14, height: "100%" }}>
      {/* LEFT COLUMN */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{ alignSelf: "flex-start", background: "#07111f", border: "1px solid #334155", color: "#38bdf8", borderRadius: 7, padding: "7px 10px", fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}
          >
            ← Back to {returnPage === "portfolio" ? "Portfolio" : returnPage === "scanner" ? "Scanner" : returnPage || "previous page"}
          </button>
        )}

        {/* Top stats */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(4,1fr)", gap: 10 }}>
          <StatCard label="BUY SIGNALS"  value={buys}  color="#22c55e" icon="↑" />
          <StatCard label="SELL SIGNALS" value={sells} color="#ef4444" icon="↓" />
          <StatCard label="NEUTRAL"      value={holds} color="#64748b" icon="—" />
          <StatCard label="OPEN POSITIONS" value={Object.keys(positions).length} color="#f59e0b" icon="◈" sub={`max ${botStatus?.maxPositions || 5}`} />
        </div>

        <section style={{ background: "#060d1a", border: `1px solid ${marketColor}55`, borderRadius: 8, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em", fontWeight: 700 }}>SYSTEM MARKET ANALYSIS</div>
              <div style={{ color: marketColor, fontSize: 16, fontWeight: 800, marginTop: 5 }}>{marketAnalysis.headline}</div>
              <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 5, maxWidth: 780 }}>{marketAnalysis.summary}</div>
              {marketAnalysis.guidance && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 5 }}>{marketAnalysis.guidance}</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span title={marketRegime?.reason || "Market regime not classified"} style={{ color: marketColor, border: `1px solid ${marketColor}66`, borderRadius: 5, padding: "5px 7px", fontSize: 10, fontWeight: 800 }}>{marketState}</span>
              <button type="button" onClick={() => refreshMarketRegime(true)} disabled={marketRegimeBusy || !botStatus?.marketOpen} style={{ background: "#07111f", border: "1px solid #334155", color: marketRegimeBusy ? "#64748b" : "#38bdf8", borderRadius: 6, padding: "6px 8px", fontSize: 10, cursor: marketRegimeBusy || !botStatus?.marketOpen ? "not-allowed" : "pointer" }}>
                {marketRegimeBusy ? "Refreshing..." : "Refresh market"}
              </button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,minmax(0,1fr))", gap: 8, marginTop: 12 }}>
            {marketIndexes.map((index) => {
              const change = Number(index?.changePct);
              const available = index?.available === true;
              const changeColor = Number.isFinite(change) ? (change >= 0 ? "#22c55e" : "#ef4444") : "#64748b";
              return <div key={index.id} style={{ border: "1px solid #1e293b", borderRadius: 6, padding: "9px 10px", minWidth: 0 }}>
                <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700 }}>{index.label}</div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 5 }}>
                  <span style={{ color: available ? "#e2e8f0" : "#64748b", fontSize: 14, fontWeight: 800 }}>{available ? `Rs ${Number(index.currentPrice).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "Unavailable"}</span>
                  <span style={{ color: changeColor, fontSize: 12, fontWeight: 800 }}>{Number.isFinite(change) ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}%` : "--"}</span>
                </div>
                <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>{available ? `Prev close Rs ${Number(index.previousClose).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : index.instrumentKey}</div>
              </div>;
            })}
          </div>
          <div style={{ color: marketRegimeError ? "#f87171" : "#64748b", fontSize: 10, marginTop: 9 }}>
            {marketRegimeError || (marketRegime?.generatedAt ? `Updated ${new Date(marketRegime.generatedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST` : "Waiting for the first market-index snapshot.")}
            {!marketRegimeError && missingMarketIndexes.length > 0 && ` | Missing quote: ${missingMarketIndexes.map((index) => index.label).join(", ")}`}
          </div>
        </section>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 240px", gap: 10 }}>
          <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>AUTO BUY STRATEGY</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {enabledStrategies.length ? enabledStrategies.map((name) => (
                <span key={name} style={{ border: "1px solid #22c55e44", color: "#22c55e", borderRadius: 5, padding: "4px 7px", fontSize: 10 }}>{name}</span>
              )) : <span style={{ color: "#64748b", fontSize: 12 }}>No enabled strategy reported by backend.</span>}
            </div>
            <div style={{ color: "#64748b", fontSize: 11, marginTop: 8 }}>
              Auto entries use scanner ranking plus these enabled strategy confirmations. Mode: {botStatus?.paperTrading ? "paper trading" : "live Upstox"}.
            </div>
          </div>
          <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600 }}>PROFIT</div>
              <select value={profitPeriod} onChange={(e) => handleProfitPeriodChange(e.target.value)} style={{ background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "5px 7px", fontSize: 10 }}>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
                <option value="since">Since date</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: profitPeriod === "custom" && !isMobile ? "1fr 1fr" : "1fr", gap: 8, marginTop: 8 }}>
              <input
                type={profitPeriod === "month" ? "month" : profitPeriod === "year" ? "number" : "date"}
                min={profitPeriod === "year" ? "2020" : undefined}
                max={profitPeriod === "year" ? currentPeriodValueIST("year") : profitPeriod === "month" ? currentPeriodValueIST("month") : profitPeriod === "custom" ? profitCustomToDate : currentPeriodValueIST("day")}
                value={profitPeriodValue}
                onChange={(e) => setProfitPeriodValue(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "7px 8px", fontSize: 11 }}
              />
              {profitPeriod === "custom" && (
                <input
                  type="date"
                  min={profitPeriodValue}
                  max={currentPeriodValueIST("day")}
                  value={profitCustomToDate}
                  onChange={(e) => setProfitCustomToDate(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "7px 8px", fontSize: 11 }}
                />
              )}
            </div>
            <div style={{ color: periodNetPnl >= 0 ? "#22c55e" : "#ef4444", fontSize: 22, fontWeight: 800, marginTop: 8 }}>
              {periodNetPnl >= 0 ? "+" : ""}Rs {periodNetPnl.toFixed(2)}
            </div>
            <div style={{ color: openNetPnl >= 0 ? "#22c55e" : "#ef4444", fontSize: 11, marginTop: 4 }}>
              Current open: {openNetPnl >= 0 ? "+" : ""}Rs {openNetPnl.toFixed(2)}
            </div>
            <div style={{ color: "#64748b", fontSize: 10, marginTop: 3 }}>
              {selectedRange.label}{selectedRange.includesPresent ? " · includes current open net P&L" : " · closed trades only"}
            </div>
          </div>
        </div>

        {/* Chart panel */}
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 16, flex: 1 }}>
          <div style={{ marginBottom: 14, position: "relative" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                placeholder="Search NSE symbol for chart..."
                value={searchQ}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ flex: "1 1 260px", minWidth: 0, background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 7, padding: "9px 11px", fontSize: 12, fontFamily: "'DM Mono',monospace", outline: "none" }}
              />
              {searchLoading && <span style={{ fontSize: 10, color: "#64748b" }}>Searching...</span>}
              {searchQ && !searchLoading && (
                <button type="button" onClick={() => { setSearchQ(""); setSearchResults([]); }} style={{ background: "#07111f", border: "1px solid #1e293b", color: "#64748b", borderRadius: 7, padding: "8px 10px", cursor: "pointer" }}>
                  Clear
                </button>
              )}
            </div>
            {searchResults.length > 0 && (
              <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, top: 42, maxHeight: 240, overflowY: "auto", background: "#020617", border: "1px solid #1e293b", borderRadius: 8, boxShadow: "0 18px 40px #0008" }}>
                {searchResults.map((m, i) => (
                  <button
                    key={m.instrumentKey || `${m.symbol}-${i}`}
                    type="button"
                    onClick={() => openSearchedSymbol(m)}
                    style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", borderBottom: "1px solid #0a1628", color: "#e2e8f0", padding: "9px 11px", cursor: "pointer" }}
                  >
                    <strong style={{ fontSize: 12 }}>{m.symbol}</strong>
                    {m.name && <span style={{ color: "#64748b", fontSize: 11, marginLeft: 8 }}>{m.name}</span>}
                    <span style={{ color: "#334155", fontSize: 9, marginLeft: 8, fontFamily: "monospace" }}>{m.instrumentKey}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
            <div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? 18 : 20, fontWeight: 700, color: "#f1f5f9", letterSpacing: "-0.02em" }}>{selected}</div>
              <button
                type="button"
                disabled={!selected || !blacklistReady || blacklistBusy}
                onClick={toggleSelectedBlacklist}
                style={{ marginTop: 8, padding: "7px 9px", borderRadius: 6, border: "1px solid #f59e0b66", background: "#1f1200", color: "#fbbf24", fontSize: 11, cursor: blacklistBusy ? "wait" : "pointer", opacity: blacklistReady && !blacklistBusy ? 1 : 0.6 }}
              >
                {blacklistBusy ? "Updating blacklist..." : !blacklistReady ? "Blacklist unavailable" : blacklistSymbols.includes(selected) ? "Remove from blacklist" : "Add to blacklist"}
              </button>
              {blacklistNotice && <div role="status" style={{ marginTop: 6, color: "#22c55e", fontSize: 11 }}>{blacklistNotice}</div>}
              {blacklistError && <div role="alert" style={{ marginTop: 6, color: "#f87171", fontSize: 11 }}>
                {blacklistError} <button type="button" onClick={refreshBlacklist} disabled={blacklistBusy}>Retry blacklist</button>
              </div>}
              <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                NSE Equity ·{" "}
                {selectedPos?.lastPriceSource === "upstox_quote" || latestQuote?.source === "upstox_quote" ? "Live LTP from Upstox quote" :
                 candleSource === "live" ? "Live price curve" :
                 candleSource === "intraday" ? `${chartRangeConfig.interval} historical curve` :
                 candleSource === "historical" ? "Daily historical curve" :
                 "No data yet"}
              </div>
              {Number.isFinite(chartReturnPct) && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
                  <span style={{
                    border: `1px solid ${chartReturnPct >= 0 ? "#22c55e66" : "#ef444466"}`,
                    background: chartReturnPct >= 0 ? "#052e1622" : "#2d0a0a22",
                    color: chartReturnPct >= 0 ? "#22c55e" : "#ef4444",
                    borderRadius: 5,
                    padding: "3px 7px",
                    fontSize: 10,
                    fontWeight: 800,
                    fontFamily: "'DM Mono',monospace",
                  }}>
                    {chartReturnPct >= 0 ? "+" : ""}{chartReturnPct.toFixed(2)}%
                  </span>
                  <span style={{ color: "#64748b", fontSize: 10 }}>
                    visible period {chartReturnAbs >= 0 ? "+" : ""}Rs {chartReturnAbs.toFixed(2)}
                  </span>
                </div>
              )}
              {selectedTradeForSymbol && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 7 }}>
                  {Number.isFinite(Number(selectedTradeForSymbol.buyPrice)) && (
                    <span style={{ border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 5, padding: "3px 7px", fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
                      BUY {Number(selectedTradeForSymbol.buyPrice).toFixed(2)}
                    </span>
                  )}
                  {Number.isFinite(Number(selectedTradeForSymbol.sellPrice)) && (
                    <span style={{ border: "1px solid #ef444466", color: "#ef4444", borderRadius: 5, padding: "3px 7px", fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
                      SELL {Number(selectedTradeForSymbol.sellPrice).toFixed(2)}
                    </span>
                  )}
                  {Number.isFinite(tradeNetPct) && (
                    <span style={{ color: tradeNetPct >= 0 ? "#22c55e" : "#ef4444", fontSize: 10, fontWeight: 700 }}>
                      trade {tradeNetPct >= 0 ? "+" : ""}{tradeNetPct.toFixed(2)}%
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={{ textAlign: isMobile ? "left" : "right", display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 8 }}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
                {Object.keys(CHART_RANGES).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setChartRange(key)}
                    style={{
                      background: chartRange === key ? "#0d1f35" : "#07111f",
                      border: `1px solid ${chartRange === key ? "#38bdf8" : "#1e293b"}`,
                      color: chartRange === key ? "#38bdf8" : "#64748b",
                      borderRadius: 6,
                      padding: "5px 8px",
                      fontSize: 10,
                      fontFamily: "'DM Mono',monospace",
                      cursor: "pointer",
                    }}
                  >
                    {CHART_RANGES[key].label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setChartMode((m) => (m === "line" ? "candles" : "line"))}
                  style={{
                    background: "#07111f",
                    border: "1px solid #334155",
                    color: "#94a3b8",
                    borderRadius: 6,
                    padding: "5px 8px",
                    fontSize: 10,
                    fontFamily: "'DM Mono',monospace",
                    cursor: "pointer",
                  }}
                >
                  {chartMode === "line" ? "Line" : "Candles"}
                </button>
              </div>
              {price && (
                <>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? 20 : 24, fontWeight: 800, color: "#f1f5f9" }}>
                    ₹{price.toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: chgPct >= 0 ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
                    {chgPct >= 0 ? "▲" : "▼"} {Math.abs(chgPct).toFixed(2)}%
                  </div>
                </>
              )}
              {selectedSig && <SignalBadge action={selectedSig.action} size="lg" />}
            </div>
          </div>

          {loading ? (
            <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#334155", fontSize: 12 }}>
              Loading chart data...
            </div>
          ) : candles.length > 5 ? (
            <CandleChart
              candles={candles}
              width={chartSize.width}
              height={chartSize.height}
              mode={chartMode}
              markers={tradeMarkers}
              onViewChange={handleChartViewChange}
            />
          ) : (
            <div style={{ height: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <div style={{ color: "#334155", fontSize: 12 }}>
                {botStatus?.authenticated
                  ? selectedInstrumentKeyForApi
                    ? "No candle data available for this symbol yet."
                    : "No candle data available. Search/open the instrument from Scanner so the dashboard has its instrument key."
                  : "Connect to Upstox in Settings to see chart data."}
              </div>
              {botStatus?.authenticated && (
                <div style={{ color: "#1e293b", fontSize: 11 }}>
                  Live candles appear during market hours (09:15–15:30 IST) when bot is active.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Signal reasons */}
        {selectedSig?.reasons?.length > 0 && (
          <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", marginBottom: 10, fontWeight: 600 }}>
              ACTIVE SIGNALS — {selected}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {selectedSig.reasons.map((r, i) => {
                const isBuy = r.toLowerCase().includes("buy") || r.toLowerCase().includes("oversold") || r.toLowerCase().includes("bullish") || r.toLowerCase().includes("above") || r.toLowerCase().includes("+");
                const isSell = r.toLowerCase().includes("sell") || r.toLowerCase().includes("overbought") || r.toLowerCase().includes("bearish") || r.toLowerCase().includes("death") || r.toLowerCase().includes("below");
                const col = isBuy ? "#22c55e" : isSell ? "#ef4444" : "#64748b";
                const bg  = isBuy ? "#052e1622" : isSell ? "#2d0a0a22" : "#0f172a";
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: bg, border: `1px solid ${col}22`, borderRadius: 6, padding: "8px 12px" }}>
                    <span style={{ color: col, fontSize: 14 }}>{isBuy ? "↑" : isSell ? "↓" : "·"}</span>
                    <span style={{ fontSize: 12, color: "#cbd5e1", flex: 1 }}>{r}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 10, color: "#475569" }}>Composite Score:</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: (selectedSig.score || 0) > 0 ? "#22c55e" : (selectedSig.score || 0) < 0 ? "#ef4444" : "#64748b" }}>
                {(selectedSig.score || 0) > 0 ? "+" : ""}{selectedSig.score}
              </span>
              <span style={{ fontSize: 10, color: "#334155", marginLeft: "auto" }}>Min ±{botStatus?.minEntryScore || 4} to trade</span>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT COLUMN — Watchlist */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: "14px 0", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: isMobile ? 420 : "none" }}>
        <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, padding: "0 14px 10px" }}>
          WATCHLIST · {watchlistSymbols.length} STOCKS
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {watchlistSymbols.map(sym => {
            const sig = signals.find(s => s.symbol === sym);
            const inPos = !!positions[sym];
            const isSelected = sym === selected;
            return (
              <div
                key={sym}
                onClick={() => setSelected(sym)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px",
                  background: isSelected ? "#0d1f35" : "transparent",
                  borderLeft: isSelected ? "2px solid #22c55e" : "2px solid transparent",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#f1f5f9" }}>{sym}</span>
                    {inPos && <span style={{ fontSize: 8, background: "#f59e0b22", border: "1px solid #f59e0b66", color: "#f59e0b", borderRadius: 3, padding: "1px 4px" }}>OPEN</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#475569", marginTop: 1 }}>
                    {watchlistMeta[sym]?.source || "watch"}{sig && (
                      <>
                        {" · "}Score: <span style={{ color: (sig.score || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{(sig.score || 0) > 0 ? "+" : ""}{sig.score || 0}</span>
                      </>
                    )}
                  </div>
                </div>
                {sig && <SignalBadge action={sig.action || "HOLD"} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
